// A RealtimeSubscriber that is usable before its implementation has loaded —
// BL-1406, so the gateway's transport library can be code-split out.
//
// WHY THIS EXISTS. `createRealtimeSubscriber` needs a subscriber SYNCHRONOUSLY
// (its caller, `getRealtimeSubscriber()`, is sync and runs inside a provider). A
// static `import` of the socket.io client to satisfy that put ~30 KB gzipped
// into bidder-web's `[tenant]` layout chunk — on EVERY page, in EVERY
// environment, including the ones whose gateway URL is empty and which never
// open a socket at all. Measured, not assumed.
//
// So the import becomes dynamic and this stands in until it resolves: callers
// get a subscriber immediately, subscriptions and connection handlers registered
// in the meantime are queued, and everything is replayed once the real one
// exists.

import type { PayloadOf, RealtimeEventName, TopicOf } from '../../types/realtime/events';
import type { RealtimeConnectionEvent, RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';

/** A subscription requested before the real subscriber existed. */
interface QueuedSubscription {
    topic: string;
    event: RealtimeEventName;
    handler: (payload: never) => void;
    /** Set when the caller disposed before the load resolved — never replayed. */
    disposed: boolean;
    /** Set once replayed, so disposal reaches the real subscriber. */
    dispose?: Unsubscribe;
}

/** A connection handler registered before the real subscriber existed. */
interface QueuedConnectionHandler {
    handler: (event: RealtimeConnectionEvent) => void;
    /** Set when the caller disposed before the load resolved — never replayed. */
    disposed: boolean;
    /** Set once replayed, so disposal reaches the real subscriber. */
    dispose?: Unsubscribe;
}

/**
 * Wrap an async-loaded subscriber in a synchronous one.
 *
 * **`onConnectionChange` is always present, even before the load resolves** —
 * and that is load-bearing, not incidental. `installReconcileTriggers` reads the
 * method ONCE at provider mount, which is inside the load window, and treats an
 * absent one as "no signal available". A method that only appeared later would
 * leave the transport-reconnect trigger uninstalled for the tab's whole life, so
 * recovery from a real outage would wait for the periodic tick instead.
 *
 * It did not always forward. While the gateway was an OBSERVER (BL-1406) this
 * method was deliberately omitted, so a transport being compared against could
 * not steer the one actually delivering. Post-cutover (BL-1495) the gateway IS
 * the delivering transport, so its reconnects are exactly the signal the
 * providers need. The storm guard lives where it always did — the leading+
 * trailing `RECONCILE_THROTTLE_MS` throttle in `install-reconcile-triggers.ts`,
 * which bounds any burst to one sweep per window.
 *
 * A load failure is swallowed: "the gateway never loaded" must degrade to a page
 * that renders from catch-up and stops updating live, never to a broken page.
 *
 * @param load - Resolves the real subscriber. Called once, immediately.
 * @returns A subscriber usable synchronously.
 *
 * @sideEffect Starts the load on construction and queues subscriptions and
 *   connection handlers until it settles. Never throws.
 */
export function createDeferredSubscriber(
    load: () => Promise<RealtimeSubscriber>,
): RealtimeSubscriber {
    let real: RealtimeSubscriber | null = null;
    // BL-2342 — offline mode may suspend before the socket.io chunk loads (a
    // cold start with no signal). Held here and handed over on load.
    let suspended = false;
    const queue: QueuedSubscription[] = [];
    const connectionQueue: QueuedConnectionHandler[] = [];

    /** Hand one queued subscription to the now-loaded subscriber. */
    function replay(q: QueuedSubscription): void {
        if (q.disposed || real == null) return;
        try {
            q.dispose = real.subscribe({
                topic: q.topic as TopicOf<RealtimeEventName>,
                event: q.event,
                handler: q.handler as (payload: PayloadOf<RealtimeEventName>) => void,
            });
        } catch {
            // Observation only — a failed replay costs comparison coverage for
            // that topic and nothing else.
        }
    }

    /** Hand one queued connection handler to the now-loaded subscriber. */
    function replayConnection(q: QueuedConnectionHandler): void {
        if (q.disposed || real == null) return;
        try {
            q.dispose = real.onConnectionChange?.(q.handler);
        } catch {
            // A transport that cannot report connection events still delivers
            // frames; the periodic reconcile tick remains as the safety net.
        }
    }

    void load()
        .then((loaded) => {
            real = loaded;
            // Suspend BEFORE replaying, so a held topic is not joined on a
            // socket that is about to close.
            if (suspended) real.setSuspended?.(true);
            for (const q of queue) replay(q);
            queue.length = 0;
            for (const q of connectionQueue) replayConnection(q);
            connectionQueue.length = 0;
        })
        .catch(() => {
            // Left null: every subsequent subscribe becomes a no-op, which is
            // exactly "no live updates" — the AC 5 degrade.
            queue.length = 0;
            connectionQueue.length = 0;
        });

    return {
        subscribe<N extends RealtimeEventName>({
            topic,
            event,
            handler,
        }: {
            topic: TopicOf<N>;
            event: N;
            handler: (payload: PayloadOf<N>) => void;
        }): Unsubscribe {
            if (real != null) {
                try {
                    return real.subscribe({ topic, event, handler });
                } catch {
                    return () => {};
                }
            }

            const queued: QueuedSubscription = {
                topic,
                event,
                handler: handler as (payload: never) => void,
                disposed: false,
            };
            queue.push(queued);

            // Disposal must work in BOTH windows — before the load resolves (mark
            // it so replay skips it) and after (forward to the real unsubscribe).
            // Getting only one right leaks a gateway room for a component that
            // unmounted during the load.
            return () => {
                queued.disposed = true;
                queued.dispose?.();
            };
        },

        onConnectionChange(handler: (event: RealtimeConnectionEvent) => void): Unsubscribe {
            if (real != null) {
                try {
                    return real.onConnectionChange?.(handler) ?? (() => {});
                } catch {
                    return () => {};
                }
            }

            const queued: QueuedConnectionHandler = { handler, disposed: false };
            connectionQueue.push(queued);

            // Disposal must work in BOTH windows — before the load resolves (mark
            // it so replay skips it) and after (forward to the real unsubscribe).
            // Getting only one right leaves a provider that unmounted mid-load
            // firing reconcileAll() against stores nobody is reading.
            return () => {
                queued.disposed = true;
                queued.dispose?.();
            };
        },

        setSuspended(next: boolean): void {
            suspended = next;
            real?.setSuspended?.(next);
        },

        // Before the load resolves there is no socket, so nothing to retry.
        reconnectNow(): void {
            real?.reconnectNow?.();
        },

        // The wrapper's own flag, not the real one's: before the load resolves
        // (or after it fails) there is no real subscriber, and the stores must
        // still see the suspension.
        isSuspended: () => suspended,
    };
}
